import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import Layout, { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Funnel,
  Plus,
  PencilSimple,
  Trash,
  Copy,
  DownloadSimple,
  ArrowsClockwise,
  ChartBar,
  CheckCircle,
  Warning,
  CaretDown,
  Key,
  FloppyDisk,
  Eye,
  EyeSlash,
  X,
} from "@phosphor-icons/react";
import { adspectApi, formatApiError } from "@/lib/api";

// ─── Constants ────────────────────────────────────────────────────────────────

const MODES = ["Filter", "Review", "Money", "White"];
const FILTER_LEVELS = [
  { value: 0, label: "Off" },
  { value: 1, label: "Low" },
  { value: 2, label: "Medium" },
  { value: 3, label: "High" },
  { value: 4, label: "Paranoid" },
];
const MONEY_ACTIONS = ["302", "301", "303", "307", "308", "proxy", "iframe", "fetch", "refresh", "meta", "noop"];
const SAFE_ACTIONS = ["local", "302", "301", "proxy", "noop", "fetch", "iframe"];

const MODE_COLORS = {
  Filter: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  Review: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  Money:  "bg-green-500/10 text-green-400 border-green-500/30",
  White:  "bg-slate-500/10 text-slate-400 border-slate-500/30",
};

const MODE_ACTIVE = {
  Filter: "bg-blue-500 text-white border-blue-500",
  Review: "bg-amber-500 text-white border-amber-500",
  Money:  "bg-green-500 text-white border-green-500",
  White:  "bg-slate-500 text-white border-slate-500",
};

const LEVEL_LABELS = ["Off", "Low", "Medium", "High", "Paranoid"];
const LEVEL_COLORS = [
  "bg-slate-500/10 text-slate-400",
  "bg-blue-500/10 text-blue-400",
  "bg-yellow-500/10 text-yellow-400",
  "bg-orange-500/10 text-orange-400",
  "bg-red-500/10 text-red-400",
];

// Date helpers (used for viewStreamLogs new-tab link)
function todayStr() { return new Date().toISOString().slice(0, 10); }

// ─── Default form state ───────────────────────────────────────────────────────

const EMPTY_FORM = {
  name: "",
  mode: "Filter",
  filter_level: 2,
  money_page_url: "",
  money_page_action: "302",
  safe_page_url: "",
  safe_page_action: "local",
  countries: "",
  os_list: "",
  tags: "",
  notes: "",
  enable_fp: true,
  enable_ua: true,
};

function streamToForm(s) {
  const mp = s.money_pages?.[0] || {};
  const sp = s.safe_pages?.[0] || {};
  return {
    name: s.name || "",
    mode: s.mode || "Filter",
    filter_level: typeof s.filter_level === "number" ? s.filter_level : 2,
    money_page_url: mp.page || "",
    money_page_action: mp.action || "302",
    safe_page_url: sp.page || "",
    safe_page_action: sp.action || "local",
    countries: (s.countries || []).join(", "),
    os_list: (s.os || []).join(", "),
    tags: (s.tags || []).join(", "),
    notes: s.notes || "",
    enable_fp: !!s.enable_fp,  // API returns 0/1 integer, convert to boolean
    enable_ua: !!s.enable_ua,
  };
}

function formToPayload(form) {
  const splitTrim = (s) => s.split(",").map((x) => x.trim().toUpperCase()).filter(Boolean);
  const splitTrimRaw = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
  return {
    name: form.name,
    mode: form.mode,
    filter_level: Number(form.filter_level),
    money_page_url: form.money_page_url,
    money_page_action: form.money_page_action,
    safe_page_url: form.safe_page_url,
    safe_page_action: form.safe_page_action,
    countries: splitTrim(form.countries),
    os_list: splitTrimRaw(form.os_list),
    tags: splitTrimRaw(form.tags),
    notes: form.notes,
    enable_fp: form.enable_fp,
    enable_ua: form.enable_ua,
  };
}

// ─── Stream Form Modal ────────────────────────────────────────────────────────

function StreamModal({ open, onClose, onSave, initialData, loading }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const isEdit = !!initialData;

  useEffect(() => {
    if (open) {
      setForm(initialData ? streamToForm(initialData) : { ...EMPTY_FORM });
    }
  }, [open, initialData]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Stream name is required"); return; }
    onSave(formToPayload(form));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="stream-modal-title">
            {isEdit ? "Edit Stream" : "Create Stream"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Row 1 */}
          <div className="space-y-1.5">
            <Label htmlFor="stream-name">Stream Name <span className="text-red-400">*</span></Label>
            <Input
              id="stream-name"
              data-testid="stream-name-input"
              placeholder="e.g. Main Filter Stream"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
            />
          </div>

          {/* Row 2 */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Mode</Label>
              <Select value={form.mode} onValueChange={(v) => set("mode", v)}>
                <SelectTrigger data-testid="stream-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MODES.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Filter Level</Label>
              <Select
                value={String(form.filter_level)}
                onValueChange={(v) => set("filter_level", Number(v))}
              >
                <SelectTrigger data-testid="stream-filter-level-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FILTER_LEVELS.map(({ value, label }) => (
                    <SelectItem key={value} value={String(value)}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Money Page */}
          <div className="p-3 rounded-md border border-border bg-muted/30 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Money Page (shown to real visitors)</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2 space-y-1.5">
                <Label>URL</Label>
                <Input
                  data-testid="money-page-url"
                  placeholder="https://offer.example.com"
                  value={form.money_page_url}
                  onChange={(e) => set("money_page_url", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Action</Label>
                <Select value={form.money_page_action} onValueChange={(v) => set("money_page_action", v)}>
                  <SelectTrigger data-testid="money-page-action-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONEY_ACTIONS.map((a) => (
                      <SelectItem key={a} value={a}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Safe Page */}
          <div className="p-3 rounded-md border border-border bg-muted/30 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Safe Page (shown to bots / moderators)</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2 space-y-1.5">
                <Label>URL / File path</Label>
                <Input
                  data-testid="safe-page-url"
                  placeholder="safe.html or https://safe.example.com"
                  value={form.safe_page_url}
                  onChange={(e) => set("safe_page_url", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Action</Label>
                <Select value={form.safe_page_action} onValueChange={(v) => set("safe_page_action", v)}>
                  <SelectTrigger data-testid="safe-page-action-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SAFE_ACTIONS.map((a) => (
                      <SelectItem key={a} value={a}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Targeting */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Countries <span className="text-muted-foreground">(ISO codes, comma-separated)</span></Label>
              <Input
                data-testid="countries-input"
                placeholder="AE, US, GB, DE"
                value={form.countries}
                onChange={(e) => set("countries", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Operating Systems <span className="text-muted-foreground">(comma-separated)</span></Label>
              <Input
                data-testid="os-input"
                placeholder="iOS, Android, Windows"
                value={form.os_list}
                onChange={(e) => set("os_list", e.target.value)}
              />
            </div>
          </div>

          {/* Tags & Notes */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Tags <span className="text-muted-foreground">(comma-separated)</span></Label>
              <Input
                data-testid="tags-input"
                placeholder="tag1, tag2"
                value={form.tags}
                onChange={(e) => set("tags", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Input
                data-testid="notes-input"
                placeholder="Optional notes"
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
              />
            </div>
          </div>

          {/* Toggles */}
          <div className="flex gap-8">
            <div className="flex items-center gap-2">
              <Switch
                id="enable-fp"
                data-testid="enable-fp-switch"
                checked={form.enable_fp}
                onCheckedChange={(v) => set("enable_fp", v)}
              />
              <Label htmlFor="enable-fp" className="cursor-pointer">JS Fingerprinting (VLA™)</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="enable-ua"
                data-testid="enable-ua-switch"
                checked={form.enable_ua}
                onCheckedChange={(v) => set("enable_ua", v)}
              />
              <Label htmlFor="enable-ua" className="cursor-pointer">UA-based Filters</Label>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="stream-modal-save" disabled={loading}>
              {loading ? "Saving…" : isEdit ? "Save Changes" : "Create Stream"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function AdspectPage() {
  const [account, setAccount] = useState(null);
  const [streams, setStreams] = useState([]);
  const [funnel, setFunnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [funnelLoading, setFunnelLoading] = useState(false);
  const [streamModal, setStreamModal] = useState(null);
  const [savingStream, setSavingStream] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [copying, setCopying] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [config, setConfig] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [testingConfig, setTestingConfig] = useState(false);

  // ── Mode switching state ──────────────────────────────────────────────────
  const [modeSwitching, setModeSwitching] = useState({}); // {stream_id: true}

  const load = useCallback(async () => {
    setLoading(true);
    const [cfgRes, streamsRes, accRes] = await Promise.allSettled([
      adspectApi.getConfig(),
      adspectApi.listStreams(),
      adspectApi.getAccount(),
    ]);
    if (cfgRes.status === "fulfilled") setConfig(cfgRes.value.data);
    if (streamsRes.status === "fulfilled") setStreams(streamsRes.value.data.streams || []);
    if (accRes.status === "fulfilled") setAccount(accRes.value.data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadFunnel = async () => {
    setFunnelLoading(true);
    try {
      const res = await adspectApi.getFunnelReport();
      setFunnel(res.data.rows || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setFunnelLoading(false);
    }
  };

  // ── Stream CRUD ──────────────────────────────────────────────────────────────

  const handleSaveStream = async (payload) => {
    setSavingStream(true);
    try {
      if (streamModal.mode === "edit") {
        await adspectApi.updateStream(streamModal.data.stream_id, payload);
        toast.success("Stream updated");
      } else {
        await adspectApi.createStream(payload);
        toast.success("Stream created");
      }
      setStreamModal(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingStream(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adspectApi.deleteStream(deleteTarget.stream_id);
      toast.success(`Stream "${deleteTarget.name}" deleted`);
      setDeleteTarget(null);
      setStreams((prev) => prev.filter((s) => s.stream_id !== deleteTarget.stream_id));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const handleCopy = async (stream) => {
    setCopying(stream.stream_id);
    try {
      await adspectApi.copyStream(stream.stream_id);
      toast.success(`Stream "${stream.name}" copied`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCopying(null);
    }
  };

  const handleDownload = async (stream, filename) => {
    const key = `${stream.stream_id}-${filename}`;
    setDownloading(key);
    try {
      await adspectApi.downloadFile(stream.stream_id, filename);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(`Failed to download ${filename}`);
    } finally {
      setDownloading(null);
    }
  };

  // ── Config ───────────────────────────────────────────────────────────────────

  const handleSaveConfig = async () => {
    if (!apiKeyInput.trim()) { toast.error("API key cannot be empty"); return; }
    setSavingConfig(true);
    try {
      await adspectApi.saveConfig({ api_key: apiKeyInput.trim() });
      toast.success("API key saved");
      setApiKeyInput("");
      setShowSettings(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingConfig(false);
    }
  };

  const handleTestConfig = async () => {
    setTestingConfig(true);
    try {
      const res = await adspectApi.testConfig();
      toast.success(`Connected — ${res.data.email} · Balance: $${res.data.balance}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setTestingConfig(false);
    }
  };

  // ── Mode switching ────────────────────────────────────────────────────────

  const handleModeChange = async (stream, newMode) => {
    if (stream.mode === newMode || modeSwitching[stream.stream_id]) return;
    setModeSwitching((prev) => ({ ...prev, [stream.stream_id]: true }));
    // Optimistic update
    setStreams((prev) =>
      prev.map((s) => s.stream_id === stream.stream_id ? { ...s, mode: newMode } : s)
    );
    try {
      await adspectApi.patchMode(stream.stream_id, newMode);
      toast.success(`"${stream.name}" switched to ${newMode}`);
    } catch (e) {
      // Revert on error
      setStreams((prev) =>
        prev.map((s) => s.stream_id === stream.stream_id ? { ...s, mode: stream.mode } : s)
      );
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setModeSwitching((prev) => { const n = { ...prev }; delete n[stream.stream_id]; return n; });
    }
  };

  // ── Inline money-page URL save ────────────────────────────────────────────

  const handleMoneyPageSave = async (stream, newUrl) => {
    const trimmed = (newUrl || "").trim();
    if (!/^https:\/\/.+/i.test(trimmed)) {
      toast.error("Money page URL must start with https://");
      return { ok: false };
    }
    const currentAction = stream.money_pages?.[0]?.action || "302";
    // Optimistic update
    setStreams((prev) =>
      prev.map((s) =>
        s.stream_id === stream.stream_id
          ? { ...s, money_pages: [{ ...(s.money_pages?.[0] || {}), page: trimmed, action: currentAction }] }
          : s
      )
    );
    try {
      await adspectApi.updateStream(stream.stream_id, {
        money_page_url: trimmed,
        money_page_action: currentAction,
      });
      toast.success(`Money page updated for "${stream.name}"`);
      return { ok: true };
    } catch (e) {
      // Revert on error
      setStreams((prev) =>
        prev.map((s) => s.stream_id === stream.stream_id ? stream : s)
      );
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to update money page");
      return { ok: false };
    }
  };

  // ── Funnel map ───────────────────────────────────────────────────────────────

  const funnelMap = {};
  funnel.forEach((r) => { funnelMap[r.stream_id] = r; });

  // Quick-open reports for a specific stream — opens in new browser tab
  const viewStreamLogs = (stream) => {
    const today = todayStr();
    const url = `/adspect/logs?stream_id=${encodeURIComponent(stream.stream_id)}&date_from=${today}&date_to=${today}`;
    window.open(url, "_blank");
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64 text-muted-foreground" data-testid="adspect-loading">
          Loading Traffic Filteration…
        </div>
      </Layout>
    );
  }

  const expiryDate = account?.expires
    ? new Date(account.expires * 1000).toLocaleDateString()
    : "—";

  return (
    <Layout>
      <div data-testid="adspect-page">
        <PageHeader
          eyebrow="Adspect"
          title="Traffic Filteration"
          description="Manage streams, filter bad traffic, and download PHP integration files."
          actions={
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                data-testid="adspect-refresh-btn"
                onClick={load}
              >
                <ArrowsClockwise size={15} className="mr-1.5" />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                data-testid="adspect-settings-btn"
                onClick={() => setShowSettings((v) => !v)}
              >
                <Key size={15} className="mr-1.5" />
                API Key
              </Button>
              <Button
                size="sm"
                data-testid="adspect-create-stream-btn"
                onClick={() => setStreamModal({ mode: "create" })}
              >
                <Plus size={15} className="mr-1.5" />
                New Stream
              </Button>
            </div>
          }
        />

        {/* ── Account Info ────────────────────────────────────────────────────── */}
        {account && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-testid="adspect-account-info">
            <InfoCard label="Email" value={account.email || "—"} />
            <InfoCard label="Balance" value={`$${account.balance || "0"}`} highlight />
            <InfoCard label="Subscription" value={expiryDate} />
            <InfoCard
              label="Status"
              value={
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${
                  config?.configured ? "bg-green-500/10 text-green-400 border-green-500/20" : "bg-red-500/10 text-red-400 border-red-500/20"
                }`}>
                  {config?.configured ? "Connected" : "Not Configured"}
                </span>
              }
            />
          </div>
        )}

        {/* ── Settings Panel ───────────────────────────────────────────────────── */}
        {showSettings && (
          <div className="mb-8 p-5 rounded-xl border border-border bg-card space-y-4" data-testid="adspect-settings-panel">
            <h3 className="text-sm font-semibold">Adspect API Key</h3>
            {config?.api_key_masked && (
              <p className="text-xs text-muted-foreground font-mono">
                Current: {config.api_key_masked}
              </p>
            )}
            <div className="flex gap-2 max-w-md">
              <div className="relative flex-1">
                <Input
                  data-testid="api-key-input"
                  type={showKey ? "text" : "password"}
                  placeholder="Enter new API key…"
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  className="pr-9"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowKey((v) => !v)}
                  tabIndex={-1}
                >
                  {showKey ? <EyeSlash size={15} /> : <Eye size={15} />}
                </button>
              </div>
              <Button
                variant="outline"
                size="sm"
                data-testid="test-config-btn"
                onClick={handleTestConfig}
                disabled={testingConfig}
              >
                {testingConfig ? "Testing…" : "Test"}
              </Button>
              <Button
                size="sm"
                data-testid="save-config-btn"
                onClick={handleSaveConfig}
                disabled={savingConfig || !apiKeyInput.trim()}
              >
                <FloppyDisk size={15} className="mr-1.5" />
                {savingConfig ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        )}

        {/* ── Streams Table ────────────────────────────────────────────────────── */}
        <section className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold" data-testid="streams-count">
              Streams
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                ({streams.length})
              </span>
            </h2>
          </div>

          {streams.length === 0 ? (
            <div
              className="border border-dashed border-border rounded-xl p-16 text-center text-muted-foreground"
              data-testid="streams-empty"
            >
              <Funnel size={40} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">No streams yet. Create one to get started.</p>
            </div>
          ) : (
            <div className="rounded-xl border border-border overflow-hidden" data-testid="streams-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/40 text-muted-foreground text-xs uppercase tracking-wide">
                    <th className="text-left px-4 py-3">Name</th>
                    <th className="text-left px-4 py-3">Mode</th>
                    <th className="text-left px-4 py-3">Filter Level</th>
                    <th className="text-left px-4 py-3">Tags</th>
                    <th className="text-left px-4 py-3">Money Page</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {streams.map((stream) => (
                    <StreamRow
                      key={stream.stream_id}
                      stream={stream}
                      funnelData={funnelMap[stream.stream_id]}
                      copying={copying === stream.stream_id}
                      downloading={downloading}
                      modeSwitching={!!modeSwitching[stream.stream_id]}
                      onModeChange={(newMode) => handleModeChange(stream, newMode)}
                      onMoneyPageSave={(url) => handleMoneyPageSave(stream, url)}
                      onEdit={() => setStreamModal({ mode: "edit", data: stream })}
                      onCopy={() => handleCopy(stream)}
                      onDelete={() => setDeleteTarget(stream)}
                      onDownload={(filename) => handleDownload(stream, filename)}
                      onViewLogs={() => viewStreamLogs(stream)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* ── Reporting ────────────────────────────────────────────────────────── */}
        <section data-testid="adspect-reports-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold">Funnel Metrics</h2>
            <Button
              variant="outline"
              size="sm"
              data-testid="load-reports-btn"
              onClick={loadFunnel}
              disabled={funnelLoading}
            >
              <ChartBar size={15} className="mr-1.5" />
              {funnelLoading ? "Loading…" : funnel.length > 0 ? "Refresh" : "Load Reports"}
            </Button>
          </div>

          {funnel.length === 0 ? (
            <div className="border border-dashed border-border rounded-xl p-12 text-center text-muted-foreground text-sm">
              Click "Load Reports" to fetch per-stream performance metrics.
            </div>
          ) : (
            <div className="rounded-xl border border-border overflow-hidden" data-testid="funnel-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/40 text-muted-foreground text-xs uppercase tracking-wide">
                    <th className="text-left px-4 py-3">Stream</th>
                    <th className="text-right px-4 py-3">Total Clicks</th>
                    <th className="text-right px-4 py-3">Money Hits</th>
                    <th className="text-right px-4 py-3">Safe Hits</th>
                    <th className="text-right px-4 py-3">Quality</th>
                  </tr>
                </thead>
                <tbody>
                  {funnel.map((row) => {
                    const stream = streams.find((s) => s.stream_id === row.stream_id);
                    const quality = (row.quality * 100).toFixed(1);
                    const qColor =
                      row.quality >= 0.7 ? "text-green-400" :
                      row.quality >= 0.4 ? "text-yellow-400" : "text-red-400";
                    return (
                      <tr
                        key={row.stream_id}
                        className="border-b border-border last:border-0 hover:bg-muted/20"
                        data-testid={`funnel-row-${row.stream_id}`}
                      >
                        <td className="px-4 py-3 font-medium">
                          {stream?.name || row.stream_id}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">{row.clicks.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-green-400">{row.money_hits.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">{row.safe_hits.toLocaleString()}</td>
                        <td className={`px-4 py-3 text-right tabular-nums font-semibold ${qColor}`}>
                          {quality}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* ── Create/Edit Modal ─────────────────────────────────────────────────── */}
        <StreamModal
          open={!!streamModal}
          onClose={() => setStreamModal(null)}
          onSave={handleSaveStream}
          initialData={streamModal?.mode === "edit" ? streamModal.data : null}
          loading={savingStream}
        />

        {/* ── Delete Confirmation ───────────────────────────────────────────────── */}
        <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle data-testid="delete-modal-title">Delete Stream?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete{" "}
              <strong className="text-foreground">"{deleteTarget?.name}"</strong>? This action
              cannot be undone.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
              <Button
                variant="destructive"
                data-testid="confirm-delete-btn"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting…" : "Delete"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

// ─── Helper Components ────────────────────────────────────────────────────────

function InfoCard({ label, value, highlight }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={`info-card-${label.toLowerCase()}`}>
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className={`text-sm font-semibold truncate ${highlight ? "text-primary" : ""}`}>
        {value}
      </p>
    </div>
  );
}

function StreamRow({ stream, funnelData, copying, downloading, modeSwitching, onModeChange, onMoneyPageSave, onEdit, onCopy, onDelete, onDownload, onViewLogs }) {
  const mp = stream.money_pages?.[0];
  const levelIdx = typeof stream.filter_level === "number" ? stream.filter_level : 2;
  const levelLabel = LEVEL_LABELS[levelIdx] ?? "Medium";
  const levelColor = LEVEL_COLORS[levelIdx] ?? LEVEL_COLORS[2];

  // Inline money-page editor state
  const [mpEditing, setMpEditing]     = useState(false);
  const [mpDraft, setMpDraft]         = useState(mp?.page || "https://");
  const [mpSaving, setMpSaving]       = useState(false);

  // Keep draft in sync when stream updates from parent (optimistic rollback etc.)
  useEffect(() => {
    if (!mpEditing) setMpDraft(mp?.page || "https://");
  }, [mp?.page, mpEditing]);

  const startEdit = () => {
    setMpDraft(mp?.page && /^https:\/\//i.test(mp.page) ? mp.page : (mp?.page ? `https://${mp.page.replace(/^https?:\/\//i, "")}` : "https://"));
    setMpEditing(true);
  };
  const cancelEdit = () => {
    setMpDraft(mp?.page || "https://");
    setMpEditing(false);
  };
  const saveEdit = async () => {
    if (mpSaving) return;
    if (!/^https:\/\/.+/i.test(mpDraft.trim())) {
      toast.error("Money page URL must start with https://");
      return;
    }
    if (mpDraft.trim() === (mp?.page || "")) {
      setMpEditing(false);
      return;
    }
    setMpSaving(true);
    const res = await onMoneyPageSave(mpDraft.trim());
    setMpSaving(false);
    if (res?.ok) setMpEditing(false);
  };

  return (
    <tr
      className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors"
      data-testid={`stream-row-${stream.stream_id}`}
    >
      <td className="px-4 py-3">
        <div className="font-medium">{stream.name}</div>
        {stream.notes && (
          <div className="text-xs text-muted-foreground truncate max-w-[200px]">{stream.notes}</div>
        )}
      </td>

      {/* Inline mode toggle — 4 pills */}
      <td className="px-4 py-3">
        <div
          className={`flex items-center gap-0.5 ${modeSwitching ? "opacity-60 pointer-events-none" : ""}`}
          data-testid={`mode-toggle-${stream.stream_id}`}
        >
          {MODES.map((m) => {
            const isActive = stream.mode === m;
            return (
              <button
                key={m}
                data-testid={`mode-pill-${stream.stream_id}-${m.toLowerCase()}`}
                onClick={() => onModeChange(m)}
                disabled={modeSwitching}
                className={`text-xs px-2 py-0.5 rounded-full border transition-all duration-150 font-medium whitespace-nowrap ${
                  isActive ? MODE_ACTIVE[m] : "bg-transparent text-muted-foreground border-transparent hover:border-border hover:text-foreground"
                }`}
                title={`Switch to ${m}`}
              >
                {m}
              </button>
            );
          })}
        </div>
      </td>

      <td className="px-4 py-3">
        <span className={`text-xs font-medium px-2 py-0.5 rounded-md ${levelColor}`}>
          {levelLabel}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {(stream.tags || []).slice(0, 3).map((t) => (
            <span key={t} className="text-xs bg-muted px-1.5 py-0.5 rounded">{t}</span>
          ))}
          {(stream.tags || []).length > 3 && (
            <span className="text-xs text-muted-foreground">+{stream.tags.length - 3}</span>
          )}
        </div>
      </td>

      {/* Inline editable Money Page */}
      <td className="px-4 py-3" data-testid={`money-page-cell-${stream.stream_id}`}>
        {mpEditing ? (
          <div className="flex items-center gap-1">
            <Input
              autoFocus
              value={mpDraft}
              onChange={(e) => setMpDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); saveEdit(); }
                else if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
              }}
              disabled={mpSaving}
              placeholder="https://example.com"
              className="h-7 text-xs font-mono min-w-[220px]"
              data-testid={`money-page-input-${stream.stream_id}`}
            />
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-green-400 hover:text-green-300 hover:bg-green-500/10"
              onClick={saveEdit}
              disabled={mpSaving}
              title="Save (Enter)"
              data-testid={`money-page-save-${stream.stream_id}`}
            >
              <FloppyDisk size={13} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-muted-foreground hover:text-foreground"
              onClick={cancelEdit}
              disabled={mpSaving}
              title="Cancel (Esc)"
              data-testid={`money-page-cancel-${stream.stream_id}`}
            >
              <X size={13} />
            </Button>
          </div>
        ) : (
          <button
            type="button"
            onClick={startEdit}
            title="Click to edit money page URL"
            className="group flex items-center gap-1.5 text-left max-w-[260px] text-xs text-muted-foreground font-mono hover:text-foreground transition-colors"
            data-testid={`money-page-display-${stream.stream_id}`}
          >
            <span className="truncate">{mp?.page || "— click to set —"}</span>
            <PencilSimple size={11} className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
          </button>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1">
          {/* View logs */}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
            data-testid={`stream-logs-btn-${stream.stream_id}`}
            onClick={onViewLogs}
            title="View click logs"
          >
            <ChartBar size={13} className="mr-1" />
            Logs
          </Button>

          {/* Download PHP files */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                data-testid={`stream-download-btn-${stream.stream_id}`}
                disabled={!!downloading}
              >
                <DownloadSimple size={13} className="mr-1" />
                PHP
                <CaretDown size={11} className="ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {[
                { file: "index.php",  label: "Forward PHP  (index.php)" },
                { file: "filter.php", label: "Reverse PHP  (filter.php)" },
                { file: "ajax.php",   label: "JavaScript   (ajax.php)"  },
              ].map(({ file, label }) => (
                <DropdownMenuItem
                  key={file}
                  data-testid={`download-${file}-${stream.stream_id}`}
                  onClick={() => onDownload(file)}
                  disabled={downloading === `${stream.stream_id}-${file}`}
                >
                  {label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2"
            data-testid={`stream-copy-btn-${stream.stream_id}`}
            onClick={onCopy}
            disabled={copying}
            title="Copy stream"
          >
            <Copy size={13} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2"
            data-testid={`stream-edit-btn-${stream.stream_id}`}
            onClick={onEdit}
            title="Edit stream"
          >
            <PencilSimple size={13} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-red-400 hover:text-red-300 hover:bg-red-500/10"
            data-testid={`stream-delete-btn-${stream.stream_id}`}
            onClick={onDelete}
            title="Delete stream"
          >
            <Trash size={13} />
          </Button>
        </div>
      </td>
    </tr>
  );
}


// ─── Log Row ──────────────────────────────────────────────────────────────────
// NOTE: LogRow moved to AdspectLogsPage.jsx — kept here only to avoid import errors
// during transition. Can be removed in future cleanup.
